import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../admin/firebase/config';

const DeliveryAuthContext = createContext();

export const useDeliveryAuth = () => {
  const context = useContext(DeliveryAuthContext);
  if (!context) {
    throw new Error('useDeliveryAuth must be used within DeliveryAuthProvider');
  }
  return context;
};

export const DeliveryAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [deliveryBoyData, setDeliveryBoyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Check if user is a delivery boy
          const userDoc = await getDoc(doc(db, 'users', user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Verify user has delivery role
            if (userData.role === 'delivery') {
              setCurrentUser(user);
              setDeliveryBoyData({
                id: user.uid,
                ...userData
              });
            } else {
              // Not a delivery boy - sign out
              await signOut(auth);
              setError('Access denied. This portal is for delivery personnel only.');
              setCurrentUser(null);
              setDeliveryBoyData(null);
            }
          } else {
            await signOut(auth);
            setError('User not found in system.');
            setCurrentUser(null);
            setDeliveryBoyData(null);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Error loading user data');
        }
      } else {
        setCurrentUser(null);
        setDeliveryBoyData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      setError('');
      setLoading(true);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Fetch user data to verify role
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        await signOut(auth);
        throw new Error('User not found in system');
      }

      const userData = userDoc.data();

      if (userData.role !== 'delivery') {
        await signOut(auth);
        throw new Error('Access denied. This portal is for delivery personnel only.');
      }

      // Success - state will be updated by onAuthStateChanged
      return { success: true };

    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Failed to login';

      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setDeliveryBoyData(null);
      setError('');
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  };

  const value = {
    currentUser,
    deliveryBoyData,
    loading,
    error,
    login,
    logout
  };

  return (
    <DeliveryAuthContext.Provider value={value}>
      {children}
    </DeliveryAuthContext.Provider>
  );
};
